"use client";

import Link from "next/link";
import { useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import ThemeToggle from "./ThemeToggle";
import { site } from "@/site.config";

/** Add a page here + create it under src/app — the sitemap reminder is in AGENTS.md. */
const LINKS: { href: string; label: string; external?: boolean }[] = [
  { href: "/projects", label: "Projects" },
  { href: "/news", label: "News" },
  { href: site.blog.url, label: "Personal Blog", external: true },
  { href: "/about", label: "About" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const reduce = useReducedMotion();

  return (
    <header className="sticky top-0 z-50 border-b border-line bg-bg">
      <nav className="mx-auto flex h-14 w-full max-w-5xl items-center justify-between px-5 md:px-8">
        <Link
          href="/"
          onClick={() => setOpen(false)}
          className="flex items-center"
          aria-label="ABMax — home"
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src="/brand/abmax-logo.png" alt="ABMax" className="h-9 w-9" />
        </Link>

        <div className="flex items-center gap-6">
          <div className="hidden items-center gap-6 md:flex">
            {LINKS.map((l) =>
              l.external ? (
                <a
                  key={l.href}
                  href={l.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-mono text-xs uppercase tracking-[0.18em] text-muted transition-colors hover:text-accent"
                >
                  {l.label}&nbsp;↗
                </a>
              ) : (
                <Link
                  key={l.href}
                  href={l.href}
                  className="font-mono text-xs uppercase tracking-[0.18em] text-muted transition-colors hover:text-accent"
                >
                  {l.label}
                </Link>
              ),
            )}
            {site.showYouTube && (
              <a
                href={site.youTubeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="font-mono text-xs uppercase tracking-[0.18em] text-muted transition-colors hover:text-accent"
              >
                YouTube&nbsp;↗
              </a>
            )}
          </div>

          <ThemeToggle />

          <button
            type="button"
            className="text-muted transition-colors hover:text-accent md:hidden"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? (
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            ) : (
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
                <path d="M4 7h16M4 12h16M4 17h16" />
              </svg>
            )}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            key="menu"
            initial={reduce ? undefined : { height: 0, opacity: 0 }}
            animate={reduce ? undefined : { height: "auto", opacity: 1 }}
            exit={reduce ? undefined : { height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="overflow-hidden border-t border-line md:hidden"
          >
            <div className="flex flex-col px-5 py-2">
              {[...LINKS, ...(site.showYouTube ? [{ href: site.youTubeUrl, label: "YouTube", external: true }] : [])].map((l) =>
                l.external ? (
                  <a
                    key={l.label}
                    href={l.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setOpen(false)}
                    className="border-b border-line py-3 font-mono text-sm uppercase tracking-[0.18em] text-muted transition-colors last:border-0 hover:text-accent"
                  >
                    {l.label}&nbsp;↗
                  </a>
                ) : (
                  <Link
                    key={l.label}
                    href={l.href}
                    onClick={() => setOpen(false)}
                    className="border-b border-line py-3 font-mono text-sm uppercase tracking-[0.18em] text-muted transition-colors last:border-0 hover:text-accent"
                  >
                    {l.label}
                  </Link>
                ),
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
